export type RestorationPhase =
  | 'disassembly_and_tagging'
  | 'chassis_and_suspension'
  | 'powertrain'
  | 'sheet_metal_and_paint'
  | 'electrical'
  | 'interior'
  | 'final_assembly';

export interface PhaseMetadata {
  id: RestorationPhase;
  sequence: number; // 1 to 7
  title: string;
  shortCode: string;
  description: string;
  suggestedMilestones: string[];
}

export const RESTORATION_PHASES: Record<RestorationPhase, PhaseMetadata> = {
  disassembly_and_tagging: {
    id: 'disassembly_and_tagging',
    sequence: 1,
    title: 'Disassembly & Tagging',
    shortCode: 'DIS',
    description: 'Vehicle teardown, cataloging parts, bagging hardware, fluid draining, and baseline photo logs.',
    suggestedMilestones: ['Complete Exterior Trim Removal', 'Glass Removal & Storage', 'Interior Teardown', 'Drivetrain Extraction', 'Chassis Unbolting']
  },
  chassis_and_suspension: {
    id: 'chassis_and_suspension',
    sequence: 2,
    title: 'Chassis & Suspension',
    shortCode: 'CHS',
    description: 'Frame blasting, powder coating, suspension rebuild, steering linkages, and braking systems.',
    suggestedMilestones: ['Frame Sandblasting & Coating', 'Front Suspension Assembly', 'Rear Axle / Differential Overhaul', 'Brake Hardline Routing']
  },
  powertrain: {
    id: 'powertrain',
    sequence: 3,
    title: 'Powertrain',
    shortCode: 'PWR',
    description: 'Engine machining, rotating assembly, transmission rebuild, cooling plumbing, and dyno tuning.',
    suggestedMilestones: ['Engine Block Machining', 'Rotating Assembly Balance & Build', 'Cylinder Head Installation', 'Transmission Overhaul', 'Engine Bay Drop-In']
  },
  sheet_metal_and_paint: {
    id: 'sheet_metal_and_paint',
    sequence: 4,
    title: 'Sheet Metal & Paint',
    shortCode: 'SMP',
    description: 'Rust cutting & patch welding, floor pans, quarter panels, epoxy primer, blocking, and base/clear coat.',
    suggestedMilestones: ['Floor Pan / Rust Repair', 'Quarter Panel Replacement', 'Epoxy Primer & Guide Coat', 'Block Sanding', 'Base/Clear Paint Application', 'Cut & Buff']
  },
  electrical: {
    id: 'electrical',
    sequence: 5,
    title: 'Electrical',
    shortCode: 'ELC',
    description: 'Main body harness, fuse blocks, electronic fuel injection (EFI) wiring, switches, gauges, and grounding.',
    suggestedMilestones: ['Main Body Harness Routing', 'Engine Management / EFI Harness', 'Gauge Cluster Wiring', 'Lighting & Switch Testing', 'Chassis Grounds Verification']
  },
  interior: {
    id: 'interior',
    sequence: 6,
    title: 'Interior',
    shortCode: 'INT',
    description: 'Sound deadening insulation, carpet, upholstery, dashboard, door cards, and weatherstripping.',
    suggestedMilestones: ['Sound Deadener / Heat Shield Install', 'New Carpet Fitting', 'Dashboard & Instrument Restoration', 'Seat Re-upholstery', 'Headliner & Door Panels']
  },
  final_assembly: {
    id: 'final_assembly',
    sequence: 7,
    title: 'Final Assembly',
    shortCode: 'ASM',
    description: 'Glass installation, exterior trim brightwork, fluids, oil priming, cam break-in, and road testing.',
    suggestedMilestones: ['Windshield & Rear Glass Install', 'Bumpers & Brightwork Trim', 'Fluids & Engine Oil Priming', 'First Start & Cam Break-In', 'Wheel Alignment & Shakedown']
  }
};

export type VehicleSystem =
  | 'chassis'
  | 'suspension'
  | 'steering'
  | 'brakes'
  | 'engine'
  | 'transmission'
  | 'drivetrain'
  | 'body'
  | 'paint'
  | 'electrical'
  | 'interior'
  | 'exhaust'
  | 'cooling'
  | 'fuel'
  | 'wheels_tires'
  | 'glass_trim';

export type SubAssemblyStatus =
  | 'cataloged'
  | 'disassembled'
  | 'cleaning_prep'
  | 'restoring'
  | 'ready_for_install'
  | 'installed';

export interface SubAssembly {
  id: string; // UUID v4
  projectId: string;
  phase: RestorationPhase;
  binNumber: string; // e.g., "BIN-C02", "SHELF-3-B4"
  name: string; // e.g., "Front Upper & Lower Control Arms"
  system: VehicleSystem;
  status: SubAssemblyStatus;
  storageLocation: string; // e.g., "Shop Rack 2, Shelf B"
  notes: string;
  hardwareBagCount?: number;
  createdAt: string; // ISO 8601
  updatedAt: string; // ISO 8601
}

export type FastenerType =
  | 'Hex Head Bolt'
  | 'Carriage Bolt'
  | 'Socket Head Cap Screw'
  | 'Phillips Truss Head Screw'
  | 'Hex Nut'
  | 'Castle Nut'
  | 'Stover Lock Nut'
  | 'Flat Washer'
  | 'Wave Washer'
  | 'Split Lock Washer'
  | 'Cotter Pin'
  | 'J-Nut / Speed Clip'
  | 'Specialty Stud';

export type FastenerGrade =
  | 'Grade 2'
  | 'Grade 5'
  | 'Grade 8'
  | 'Stainless 304'
  | 'Stainless 316'
  | 'Metric 8.8'
  | 'Metric 10.9'
  | 'OEM Induction Hardened';

export type FastenerFinish =
  | 'Black Oxide'
  | 'Yellow Cadmium'
  | 'Clear Zinc'
  | 'Phosphate & Oil'
  | 'Chrome'
  | 'Raw / As-Found'
  | 'Painted';

export type HardwareBagStatus =
  | 'tagged'
  | 'cleaning_prep'
  | 'plated_restored'
  | 'replaced'
  | 'reinstalled';

export interface FastenerSpec {
  id: string;
  type: FastenerType | string;
  size: string; // e.g., "3/8-16 x 1-1/4\"", "7/16-20", "M10x1.5 x 35mm"
  grade: FastenerGrade | string;
  finish: FastenerFinish | string;
  quantityOriginal: number;
  quantityUsable: number;
  replacementRequired: boolean;
  headmarkNotes?: string; // e.g., "TR with 6 radial tick marks"
}

export interface HardwareBag {
  id: string; // UUID v4
  subAssemblyId: string;
  projectId: string;
  bagLabel: string; // e.g., "BAG-042: Front Sway Bar Bushing Brackets & End Links"
  binLocation: string; // e.g., "BIN-A01"
  fasteners: FastenerSpec[];
  torqueSpec?: string; // e.g., "35 lb-ft with blue threadlocker"
  notes: string;
  reusable: boolean;
  replacementsOrdered: boolean;
  status: HardwareBagStatus;
  createdAt: string;
  updatedAt: string;
}

export type PhotoCategory =
  | 'teardown_as_found'
  | 'disassembly_routing'
  | 'restoration_in_progress'
  | 'completed_restored'
  | 'before_after_pair';

export interface PhotoLog {
  id: string; // UUID v4
  projectId: string;
  phase: RestorationPhase;
  subAssemblyId?: string;
  category: PhotoCategory;
  title: string;
  description: string;
  beforeImageUrl: string;
  afterImageUrl?: string;
  capturedAt: string; // ISO 8601
  tags: string[]; // e.g. ["vin_plate", "firewall", "wiring_harness"]
  includeInDossier: boolean; // Flag to feature in R5 Agreed-Value Dossier
  milestoneProof: boolean;
  orientation?: 'landscape' | 'portrait';
  createdAt: string;
}

export type TaskStatus = 'todo' | 'in_progress' | 'blocked' | 'completed';
export type TaskPriority = 'low' | 'medium' | 'high' | 'critical';

export interface RestorationTask {
  id: string;
  projectId: string;
  phase: RestorationPhase;
  subAssemblyId?: string;
  title: string;
  description: string;
  status: TaskStatus;
  priority: TaskPriority;
  estimatedHours: number;
  actualHours: number;
  assignedTo?: string;
  blockingReason?: string;
  completedAt?: string;
  createdAt: string;
  updatedAt: string;
}
